import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Checkbox, Input } from '@nextui-org/react';
import { CtyButton, ErrorBoundary, PageHeader } from '@circuitry-ai/doc-ui';
import {
  EditorField,
  EditorNotice,
  EditorSection,
  SelectableOptionCard,
  SettingToggle,
  SingleSelect,
} from './WorkQueueEditorParts';
import { RoutingMethodOptions } from './RoutingMethodOptions';
import {
  createEmptyWorkQueueDraft,
  createRuleId,
  getInitials,
  getWorkQueueDraftSummary,
  isWorkQueueDraftComplete,
  parseNumberInput,
  workQueueToDraft,
  type WorkQueueDraft,
} from './work-queue-editor-utils';
import {
  WORK_QUEUE_ROUTING_METHODS,
  type WorkQueue,
  type WorkQueueEntityType,
  type WorkQueueRoutingMethod,
  type WorkQueueRoutingOptions,
  type WorkQueueTeamRole,
  type WorkQueueTeamRoleMember,
} from './work-queues-types';

const PageHeaderWithErrorBoundary = ErrorBoundary(PageHeader);

const ACCESS_MANAGEMENT_PATH = '/settings/FGA';

type WorkQueueEditorProps = Readonly<{
  /** Queue being edited; omit to create a new queue */
  queue?: WorkQueue;
  entityTypes: WorkQueueEntityType[];
  teamRoles: WorkQueueTeamRole[];
  /** Names already used by other queues, for duplicate checks */
  existingQueueNames: string[];
  /** True while the create/update request is in flight */
  isSaving?: boolean;
  /** Users who have the role (core.directory.user) */
  loadRoleMembers: (
    role: WorkQueueTeamRole,
    signal?: AbortSignal
  ) => Promise<WorkQueueTeamRoleMember[]>;
  onCancel: () => void;
  onSave: (draft: WorkQueueDraft) => void;
}>;

function DraftSummary({
  draft,
  entityTypes,
  roleLabel,
}: Readonly<{
  draft: WorkQueueDraft;
  entityTypes: WorkQueueEntityType[];
  roleLabel: string;
}>) {
  const summary = getWorkQueueDraftSummary(draft, entityTypes);
  if (!summary.complete) {
    return <span>Add {summary.missing.join(', ')} to save</span>;
  }
  return (
    <span>
      <b className="font-medium text-foreground">{summary.entityName}</b> →{' '}
      <b className="font-medium text-foreground">{summary.methodLabel}</b>{' '}
      assignment to{' '}
      <b className="font-medium text-foreground">{summary.memberCount}</b>{' '}
      {summary.memberCount === 1 ? 'member' : 'members'} of{' '}
      <b className="font-medium text-foreground">{roleLabel}</b>
    </span>
  );
}

export function WorkQueueEditor({
  queue,
  entityTypes,
  teamRoles,
  existingQueueNames,
  isSaving = false,
  loadRoleMembers,
  onCancel,
  onSave,
}: WorkQueueEditorProps) {
  const navigate = useNavigate();
  const isNew = !queue;
  const rootRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState<WorkQueueDraft>(() =>
    queue ? workQueueToDraft(queue) : createEmptyWorkQueueDraft()
  );
  const [nameError, setNameError] = useState('');
  const [roleMembers, setRoleMembers] = useState<WorkQueueTeamRoleMember[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [membersError, setMembersError] = useState('');
  // A role picked in the form starts with all its users selected; the initial (edit) role keeps saved members
  const selectAllOnLoadRef = useRef(false);

  useEffect(() => {
    rootRef.current?.scrollIntoView({ block: 'start' });
  }, []);

  const updateDraft = useCallback((patch: Partial<WorkQueueDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
  }, []);

  const updateRoutingOptions = useCallback(
    (patch: Partial<WorkQueueRoutingOptions>) => {
      setDraft((current) => ({
        ...current,
        routing_options: { ...current.routing_options, ...patch },
      }));
    },
    []
  );

  const roleOptions = useMemo(
    () =>
      teamRoles.map((role) => ({
        key: role.name,
        label: role.label ?? role.name,
      })),
    [teamRoles]
  );
  // Escalations target a role by record id (escalationPolicies[].target.roleId), not by key
  const escalationRoleOptions = useMemo(
    () =>
      teamRoles.map((role) => ({
        key: role.id,
        label: role.label ?? role.name,
      })),
    [teamRoles]
  );
  const selectedRoleLabel =
    roleOptions.find((option) => option.key === draft.team_role)?.label ??
    draft.team_role;
  const selectedRole = teamRoles.find((role) => role.name === draft.team_role);

  useEffect(() => {
    setRoleMembers([]);
    setMembersError('');
    if (!selectedRole) {
      return undefined;
    }
    const controller = new AbortController();
    setIsLoadingMembers(true);
    loadRoleMembers(selectedRole, controller.signal)
      .then((members) => {
        const memberIds = members.map((member) => member.user_id);
        const selectAll = selectAllOnLoadRef.current;
        selectAllOnLoadRef.current = false;
        setRoleMembers(members);
        // Drop selections that aren't users of this role (e.g. removed from the role since saving)
        setDraft((current) => ({
          ...current,
          member_ids: selectAll
            ? memberIds
            : current.member_ids.filter((id) => memberIds.includes(id)),
        }));
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          console.error('Failed to load role members', error);
          setMembersError(
            error instanceof Error ? error.message : 'Failed to load members.'
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoadingMembers(false);
        }
      });
    return () => controller.abort();
  }, [selectedRole, loadRoleMembers]);
  const selectedMembers = useMemo(
    () =>
      roleMembers.filter((member) => draft.member_ids.includes(member.user_id)),
    [roleMembers, draft.member_ids]
  );
  const selectedEntityType = entityTypes.find(
    (entityType) => entityType.uuid === draft.entity_type
  );
  const routingFields = selectedEntityType?.routing_fields ?? [];

  const handleEntityTypeSelect = (entityTypeUuid: string) => {
    // Rules match on the entity's routing fields, so they no longer apply to a different entity type
    setDraft((current) => ({
      ...current,
      entity_type: entityTypeUuid,
      routing_options: { ...current.routing_options, rules: [] },
    }));
  };

  const handleRoleChange = (teamRole: string) => {
    selectAllOnLoadRef.current = true;
    updateDraft({ team_role: teamRole, member_ids: [] });
  };

  const handleMemberToggle = (userId: string, isSelected: boolean) => {
    setDraft((current) => ({
      ...current,
      member_ids: isSelected
        ? [...current.member_ids, userId]
        : current.member_ids.filter((memberId) => memberId !== userId),
    }));
  };

  const handleMethodSelect = (method: WorkQueueRoutingMethod) => {
    setDraft((current) => {
      const needsStarterRule =
        method === 'rules_based' && current.routing_options.rules.length === 0;
      const starterRule = {
        rule_id: createRuleId(),
        field: routingFields[0] ?? '',
        operator: 'is' as const,
        value: '',
        assign_to: current.member_ids[0] ?? '',
      };
      return {
        ...current,
        routing_method: method,
        routing_options: needsStarterRule
          ? { ...current.routing_options, rules: [starterRule] }
          : current.routing_options,
      };
    });
  };

  const handleSave = () => {
    const trimmedName = draft.name.trim();
    const isDuplicate = existingQueueNames.some(
      (name) => name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (isDuplicate) {
      setNameError(
        `A queue named "${trimmedName}" already exists. Choose a different name.`
      );
      document.getElementById('wq-name')?.focus();
      return;
    }
    onSave(draft);
  };

  const renderTeamMembers = () => {
    if (!draft.team_role) {
      return (
        <EditorNotice icon="solar:user-linear">
          Select a role to see who can be assigned work from this queue.
        </EditorNotice>
      );
    }
    if (isLoadingMembers) {
      return (
        <EditorNotice icon="solar:users-group-rounded-linear">
          Loading members of{' '}
          <b className="font-medium text-foreground">{selectedRoleLabel}</b>…
        </EditorNotice>
      );
    }
    if (membersError) {
      return (
        <EditorNotice icon="solar:danger-circle-linear">
          Couldn&apos;t load members of{' '}
          <b className="font-medium text-foreground">{selectedRoleLabel}</b>:{' '}
          {membersError}
        </EditorNotice>
      );
    }
    if (roleMembers.length === 0) {
      return (
        <EditorNotice icon="solar:danger-circle-linear">
          No users have the{' '}
          <b className="font-medium text-foreground">{selectedRoleLabel}</b>{' '}
          role yet.{' '}
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={() => navigate(ACCESS_MANAGEMENT_PATH)}
          >
            Assign users in Access Management
          </button>
          {', then come back.'}
        </EditorNotice>
      );
    }
    return (
      <EditorField
        label="Queue Members"
        endContent={
          <span className="text-sm text-default-500 tabular-nums">
            {draft.member_ids.length} of {roleMembers.length} selected
          </span>
        }
      >
        <div className="rounded-lg border border-default-200 divide-y divide-default-200">
          {roleMembers.map((member) => (
            <Checkbox
              key={member.user_id}
              isSelected={draft.member_ids.includes(member.user_id)}
              onValueChange={(isSelected) =>
                handleMemberToggle(member.user_id, isSelected)
              }
              classNames={{
                base: 'flex w-full max-w-full m-0 px-4 py-2.5 gap-1 hover:bg-default-50',
                label: 'w-full',
              }}
            >
              <span className="flex w-full items-center gap-3">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-xs font-medium shrink-0">
                  {getInitials(member.name)}
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm text-foreground truncate">
                    {member.name}
                  </span>
                  {member.email && (
                    <span className="block text-xs text-default-500 truncate">
                      {member.email}
                    </span>
                  )}
                </span>
                {member.open_items !== undefined && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs bg-default-100 text-default-700 tabular-nums whitespace-nowrap">
                    {member.open_items} open
                  </span>
                )}
              </span>
            </Checkbox>
          ))}
        </div>
      </EditorField>
    );
  };

  return (
    <div ref={rootRef} className="flex flex-col gap-4 min-h-0">
      <div className="flex flex-col gap-1">
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-1.5 text-[13px] text-default-500"
        >
          <button
            type="button"
            className="text-primary hover:underline"
            onClick={onCancel}
          >
            Work Queues
          </button>
          <span>/</span>
          <span className="truncate">{isNew ? 'New queue' : queue.name}</span>
        </nav>
        <PageHeaderWithErrorBoundary
          heading={isNew ? 'Create Work Queue' : 'Edit Work Queue'}
          description="Queues decide which records a team sees and who works each one"
          pageHeaderClass="!px-0 !gap-1 !pb-0"
        />
      </div>

      <EditorSection
        step={1}
        title="Queue details"
        description="Name the queue and pick the record type it routes"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <EditorField label="Queue Name" htmlFor="wq-name" required>
            <Input
              id="wq-name"
              variant="bordered"
              placeholder="e.g. Virtual Inspections"
              value={draft.name}
              isInvalid={!!nameError}
              errorMessage={nameError}
              onValueChange={(name) => {
                setNameError('');
                updateDraft({ name });
              }}
            />
          </EditorField>
          <EditorField label="Description" htmlFor="wq-description">
            <Input
              id="wq-description"
              variant="bordered"
              placeholder="What lands in this queue"
              value={draft.description}
              onValueChange={(description) => updateDraft({ description })}
            />
          </EditorField>
        </div>
        <EditorField label="Entity Type" required>
          <div
            role="radiogroup"
            aria-label="Entity Type"
            className="grid gap-2.5 grid-cols-[repeat(auto-fill,minmax(min(100%,210px),1fr))]"
          >
            {entityTypes.map((entityType) => (
              <SelectableOptionCard
                key={entityType.uuid}
                isSelected={draft.entity_type === entityType.uuid}
                isDisabled={!entityType.is_enabled}
                onSelect={() => handleEntityTypeSelect(entityType.uuid)}
                icon={entityType.icon}
                title={entityType.name}
                description={
                  entityType.is_enabled
                    ? entityType.source
                    : 'Turned off in Entity Types'
                }
              />
            ))}
          </div>
        </EditorField>
      </EditorSection>

      <EditorSection
        step={2}
        title="Team"
        description="Choose a role. Everyone in that role can be added to the queue."
      >
        <EditorField
          label="Team Role"
          htmlFor="wq-team-role"
          required
          className="max-w-[420px]"
        >
          <SingleSelect
            id="wq-team-role"
            ariaLabel="Team Role"
            placeholder="Select a role"
            options={roleOptions}
            value={draft.team_role}
            onChange={handleRoleChange}
          />
        </EditorField>
        {renderTeamMembers()}
      </EditorSection>

      <EditorSection
        step={3}
        title="Routing"
        description="How new items are assigned to queue members"
      >
        <div
          role="radiogroup"
          aria-label="Routing method"
          className="grid gap-2.5 grid-cols-[repeat(auto-fill,minmax(min(100%,210px),1fr))]"
        >
          {WORK_QUEUE_ROUTING_METHODS.map((method) => (
            <SelectableOptionCard
              key={method.key}
              isSelected={draft.routing_method === method.key}
              onSelect={() => handleMethodSelect(method.key)}
              icon={method.icon}
              title={method.label}
              description={method.description}
              footer={method.bestFor}
              tag={method.tag}
            />
          ))}
        </div>
        {draft.routing_method && (
          <RoutingMethodOptions
            method={draft.routing_method}
            options={draft.routing_options}
            onChange={updateRoutingOptions}
            routingFields={routingFields}
            members={selectedMembers}
            roleOptions={roleOptions}
          />
        )}
      </EditorSection>

      <EditorSection
        step={4}
        title="Service levels"
        description="Targets, escalation and reminders"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <EditorField label="Complete within (hours)" htmlFor="wq-sla">
            <Input
              id="wq-sla"
              type="number"
              min={1}
              variant="bordered"
              value={draft.sla_hours === null ? '' : String(draft.sla_hours)}
              onValueChange={(value) =>
                updateDraft({ sla_hours: parseNumberInput(value) })
              }
            />
          </EditorField>
          <EditorField
            label="Escalate if not started after (hours)"
            htmlFor="wq-escalation-hours"
          >
            <Input
              id="wq-escalation-hours"
              type="number"
              min={1}
              variant="bordered"
              value={
                draft.escalation_hours === null
                  ? ''
                  : String(draft.escalation_hours)
              }
              onValueChange={(value) =>
                updateDraft({ escalation_hours: parseNumberInput(value) })
              }
            />
          </EditorField>
          <EditorField label="Escalate to role" htmlFor="wq-escalation-role">
            <SingleSelect
              id="wq-escalation-role"
              ariaLabel="Escalate to role"
              placeholder="Select a role"
              options={escalationRoleOptions}
              value={draft.escalation_role}
              onChange={(escalation_role) => updateDraft({ escalation_role })}
            />
          </EditorField>
        </div>
        <div>
          <SettingToggle
            title="Send inspection reminders"
            description="Remind the assignee as the due time approaches"
            isSelected={draft.send_reminders}
            onValueChange={(send_reminders) => updateDraft({ send_reminders })}
          />
          <SettingToggle
            title="Queue is active"
            description="Paused queues stop receiving new items"
            isSelected={draft.is_active}
            onValueChange={(is_active) => updateDraft({ is_active })}
          />
        </div>
      </EditorSection>

      <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 border-t border-default-200 bg-background py-3.5">
        <span className="text-[13px] text-default-500">
          <DraftSummary
            draft={draft}
            entityTypes={entityTypes}
            roleLabel={selectedRoleLabel}
          />
        </span>
        <div className="flex gap-2.5">
          <CtyButton
            size="md"
            variant="bordered"
            isDisabled={isSaving}
            onPress={onCancel}
          >
            Cancel
          </CtyButton>
          <CtyButton
            size="md"
            color="primary"
            variant="solid"
            isDisabled={!isWorkQueueDraftComplete(draft) || isSaving}
            isLoading={isSaving}
            onPress={handleSave}
          >
            {isNew ? 'Create Queue' : 'Save Queue'}
          </CtyButton>
        </div>
      </div>
    </div>
  );
}

export default WorkQueueEditor;
